// /app-reporting — jak klienti používají klientský portál.
//
// 13.9.2026 (fáze 4f, rozhodnutí Č2): stránka se dělí přepínačem na tři části,
// Kdo chodí, Co dělají a Kdo vypadl. Dřív byla všechna data o přihlášeních pod
// sebou a to nejzajímavější, tedy co klienti v aplikaci opravdu dělají, bylo
// zavřené v rozklikávací sekci úplně dole a ukazovalo technické názvy z kódu
// jako jidelnicek_zadost. Teď má každá část vlastní karty se vzorníkovou
// hlavičkou a akce i sekce mají česká jména, seskupená podle části aplikace.
//
// Zdroje: /api/app-reporting (přihlášení přes SMS, admin impersonace se
// nezapočítává) a /api/behavioral-analytics (události z portálu).

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Smartphone, Users, TrendingUp, TrendingDown, Activity, AlertTriangle, Calendar,
  Clock, Award, RefreshCw, MousePointerClick, LayoutGrid,
} from "lucide-react";
import InfoTip from "../components/InfoTip.jsx";
import GradientHeader from "../components/GradientHeader.jsx";
import { Stranka, Zalozky, RozbalovaciVyber, Dlazdice, ErrorNote, Hlaska, SkeletonCards } from "../components/ui.jsx";
import { KartaPrehledu, HlavickaDlazdice, SkupinaPrehledu, Pilulka } from "../components/PrehledDlazdice.jsx";
import { useApiData } from "../lib/api.js";
import { fmt, formatDate as fmtDate, formatDateTime } from "../lib/format.js";
import { useDemoMode } from "../lib/DemoModeContext.jsx";

const DOW_NAMES_CZ = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];

// Jména sekcí portálu. Klíče jsou hodnoty, které portál posílá jako page_view.
const SEKCE_APLIKACE = {
  dashboard: "Nástěnka",
  measurements: "Měření",
  denik: "Deník",
  jidelnicek: "Jídelníček",
  leaderboard: "Žebříček",
  achievements: "Odznaky",
  bootcamp: "Bootcamp",
};

// Jména akcí a část aplikace, do které patří. Klíče jsou hodnoty, které portál
// posílá jako action. Co tu není, spadne do skupiny Ostatní pod svým názvem.
const AKCE_APLIKACE = {
  masterplan_mark: { nazev: "Odškrtli úkol", skupina: "Masterplán" },
  masterplan_toggle: { nazev: "Rozbalili masterplán", skupina: "Masterplán" },
  jidelnicek_zadost: { nazev: "Požádali o jídelníček", skupina: "Jídelníček" },
  jid_recept_navrh: { nazev: "Navrhli recept", skupina: "Jídelníček" },
  denik_react: { nazev: "Reagovali v deníku", skupina: "Deník" },
  open_leaderboard: { nazev: "Otevřeli žebříček", skupina: "Žebříček" },
  toggle_leaderboard_opt_in: { nazev: "Přepnuli účast v žebříčku", skupina: "Žebříček" },
  profile_photo_upload: { nazev: "Nahráli fotku profilu", skupina: "Profil" },
  qr_platba: { nazev: "Otevřeli QR platbu", skupina: "Platby" },
  bootcamp_tina_cta: { nazev: "Bootcamp s Tinou", skupina: "Nábor a akce" },
  bootcamp_michal_cta: { nazev: "Bootcamp s Michalem", skupina: "Nábor a akce" },
  aktivni_vikend_cta: { nazev: "Aktivní víkend", skupina: "Nábor a akce" },
  summer_kids_cta: { nazev: "Letní kemp pro děti", skupina: "Nábor a akce" },
  notif_click: { nazev: "Klikli na notifikaci", skupina: "Ovládání" },
  click_refresh: { nazev: "Obnovili data", skupina: "Ovládání" },
  open_info_modal: { nazev: "Otevřeli nápovědu", skupina: "Ovládání" },
  tab_change: { nazev: "Přepnuli měsíc a rok", skupina: "Ovládání" },
};

// Pořadí skupin akcí. Ostatní je vždycky poslední.
const PORADI_SKUPIN = ["Masterplán", "Jídelníček", "Deník", "Žebříček", "Profil", "Platby", "Nábor a akce", "Ovládání", "Ostatní"];

function formatRelTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1) return "dnes";
  if (days === 1) return "včera";
  if (days < 7) return `před ${days} dny`;
  if (days < 30) return `před ${Math.floor(days / 7)} týdny`;
  if (days < 365) return `před ${Math.floor(days / 30)} měsíci`;
  return `před ${Math.floor(days / 365)} lety`;
}

/** Řádek klienta v seznamu. Odkaz vede na jeho kartu v Klientech. */
function RadekKlienta({ jmeno, doplnky }) {
  const odkaz = jmeno ? `/clients?search=${encodeURIComponent(jmeno)}&open=1` : "#";
  return (
    <Link to={odkaz} className="flex items-center gap-2 px-4 py-2.5 transition-colors sm:px-5 sm:hover:bg-slate-50">
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800 demo-sensitive">{jmeno || "—"}</span>
      {doplnky}
    </Link>
  );
}

export default function AppReporting() {
  const { data, loading, error, refetch } = useApiData("/api/app-reporting");
  const { blurred } = useDemoMode();
  const [sekce, setSekce] = useState("chodi"); // chodi / delaji / vypadli
  const [windowDays, setWindowDays] = useState(30); // 30 nebo 90 dní

  const kpi = data?.kpi || {};
  const topActive = data?.topActive || [];
  const allTimeActive = data?.allTimeActive || [];
  const dropoff = data?.dropoff || [];
  const neverLogged = data?.neverLogged || [];
  const daily = data?.daily || [];
  const hourly = data?.hourly || [];
  const dayOfWeek = data?.dayOfWeek || [];
  const debug = data?._debug || null;
  const tableEmpty = debug && (!debug.login_events_total_rows || debug.login_events_total_rows === 0);
  const appOnboardingStats = data?.appOnboardingStats || null;

  const topActiveFiltered = useMemo(() => {
    if (windowDays === 90) return topActive;
    const cutoff = Date.now() - windowDays * 86400000;
    return topActive.filter((c) => c.last_login && new Date(c.last_login).getTime() > cutoff);
  }, [topActive, windowDays]);

  // Denní graf, chybějící dny doplněné nulou.
  const dailyFilled = useMemo(() => {
    if (daily.length === 0) return [];
    const map = new Map(daily.map((d) => [d.day, d]));
    const out = [];
    const today = new Date();
    for (let i = windowDays - 1; i >= 0; i--) {
      const dt = new Date(today.getTime() - i * 86400000);
      const key = dt.toISOString().substring(0, 10);
      const row = map.get(key);
      out.push({ day: key, label: fmtDate(dt), logins: row ? row.logins : 0, unique_users: row ? row.unique_users : 0 });
    }
    return out;
  }, [daily, windowDays]);

  const hourlyFilled = useMemo(() => {
    const map = new Map(hourly.map((h) => [h.hour, h.logins]));
    return Array.from({ length: 24 }, (_, i) => ({ hour: i, label: `${i}h`, logins: map.get(i) || 0 }));
  }, [hourly]);

  const dowFilled = useMemo(() => {
    const map = new Map(dayOfWeek.map((d) => [d.dow, d.logins]));
    const order = [1, 2, 3, 4, 5, 6, 0];
    return order.map((d) => ({ dow: d, label: DOW_NAMES_CZ[d], logins: map.get(d) || 0 }));
  }, [dayOfWeek]);

  return (
    <Stranka className={blurred ? "demo-blur" : ""}>
      <GradientHeader
        icon={Smartphone}
        title="App reporting"
        napoveda="Jak klienti používají klientský portál na aplikace.fitway40.cz. Počítají se jen přihlášení přes SMS, admin impersonace se nezapočítává."
        loading={loading}
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading }]}
      />

      <Zalozky
        stitek="Část"
        aria-label="Část reportu"
        value={sekce}
        onChange={setSekce}
        items={[
          { key: "chodi", label: "Chodí" },
          { key: "delaji", label: "Dělají" },
          { key: "vypadli", label: "Vypadli" },
        ]}
      />

      {sekce === "chodi" && (
        <RozbalovaciVyber
          stitek="Období"
          aria-label="Období"
          value={String(windowDays)}
          onChange={(k) => setWindowDays(Number(k))}
          items={[
            { key: "30", label: "Posledních 30 dní" },
            { key: "90", label: "Posledních 90 dní" },
          ]}
        />
      )}

      {error && <ErrorNote onRetry={refetch}>Analytiku se nepodařilo načíst: {error}</ErrorNote>}

      {loading && !data && <SkeletonCards count={4} />}

      {data && tableEmpty && (
        <Hlaska ton="pozor" title="Zatím žádná data o přihlášeních">
          Sledování už běží, statistika se začne plnit, jakmile se klient znovu přihlásí přes SMS.
        </Hlaska>
      )}

      {data && sekce === "chodi" && (
        <>
          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Users}
              nazev="Kdo chodí"
              podtitulek={`přihlášení za ${windowDays} dní`}
              napoveda="Přihlášení do klientského portálu. Počítají se jen SMS ověření, admin impersonace se nezapočítává."
            />
            <div className="grid grid-cols-2 gap-2 p-3 sm:p-4 lg:grid-cols-3">
              <Dlazdice
                ton="info"
                popisek={<>Aktivní 7 dní<InfoTip text="Unikátní klienti, kteří se přihlásili do aplikace za posledních 7 dní." /></>}
                hodnota={fmt(kpi.unique_users_7d || 0)}
              />
              <Dlazdice
                ton="ok"
                popisek={<>Aktivní {windowDays} dní<InfoTip text={`Unikátní klienti za posledních ${windowDays} dní. Období se přepíná nad kartou.`} /></>}
                hodnota={fmt((windowDays === 30 ? kpi.unique_users_30d : kpi.unique_users_90d) || 0)}
              />
              <Dlazdice
                ton="znacka"
                popisek={<>Přihlášení tento měsíc<InfoTip text="Počet úspěšných přihlášení přes SMS v tomto kalendářním měsíci." /></>}
                hodnota={fmt(kpi.logins_this_month || 0)}
                doplnky={kpi.mom_delta_pct != null ? (
                  <Pilulka>{kpi.mom_delta_pct >= 0 ? "+" : ""}{kpi.mom_delta_pct} % proti minulému</Pilulka>
                ) : null}
              />
              <Dlazdice
                ton="neutralni"
                popisek={<>Minulý měsíc<InfoTip text="Počet úspěšných přihlášení přes SMS v předchozím kalendářním měsíci." /></>}
                hodnota={fmt(kpi.logins_last_month || 0)}
              />
              <Dlazdice
                ton={kpi.adoption_pct >= 70 ? "ok" : kpi.adoption_pct >= 40 ? "pozor" : "problem"}
                popisek={<>Kolik klientů appku má<InfoTip text="Procento aktivních klientů, kteří se aspoň jednou přihlásili do aplikace. Sto procent znamená, že ji používají všichni." /></>}
                hodnota={`${kpi.adoption_pct || 0} %`}
                doplnky={<Pilulka>{kpi.adoption_active_with_login || 0} z {kpi.adoption_total_active || 0}</Pilulka>}
              />
              <Dlazdice
                ton="zlata"
                popisek={<>Přihlášení celkem<InfoTip text="Součet všech přihlášení přes SMS od zavedení sledování." /></>}
                hodnota={fmt(kpi.total_logins_ever || 0)}
              />
            </div>
          </KartaPrehledu>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={TrendingUp}
              nazev="Přihlášení po dnech"
              napoveda="Počet přihlášení po dnech. Plocha je celkový počet přihlášení, tenčí čára jsou unikátní klienti v ten den."
              odznak={`${windowDays} dní`}
            />
            <div className="p-3 sm:p-4">
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={dailyFilled} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip formatter={(v, name) => [v, name === "logins" ? "Přihlášení" : "Unikátních klientů"]} labelStyle={{ fontWeight: 600 }} />
                  <Area type="monotone" dataKey="logins" stroke="#6E519F" fill="#6E519F" fillOpacity={0.2} strokeWidth={2} />
                  <Area type="monotone" dataKey="unique_users" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={1.5} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </KartaPrehledu>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <KartaPrehledu>
              <HlavickaDlazdice ikona={Clock} nazev="Kdy během dne" napoveda="Rozložení přihlášení po hodinách, pražský čas. Ukazuje, kdy klienti aplikaci nejčastěji otevírají." />
              <div className="p-3 sm:p-4">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={hourlyFilled} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={2} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip formatter={(v) => [v, "Přihlášení"]} />
                    <Bar dataKey="logins" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </KartaPrehledu>

            <KartaPrehledu>
              <HlavickaDlazdice ikona={Calendar} nazev="Který den v týdnu" napoveda="Rozložení přihlášení od pondělí do neděle." />
              <div className="p-3 sm:p-4">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={dowFilled} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip formatter={(v) => [v, "Přihlášení"]} />
                    <Bar dataKey="logins" fill="#10b981" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </KartaPrehledu>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <KartaPrehledu>
              <HlavickaDlazdice
                ikona={Activity}
                nazev={`Aktivní za ${windowDays} dní`}
                napoveda={`Klienti, kteří se přihlásili do aplikace za posledních ${windowDays} dní, od nejaktivnějších. Klepnutím se otevře karta klienta.`}
                odznak={topActiveFiltered.length}
              />
              {topActiveFiltered.length === 0 ? (
                <div className="py-6 text-center text-sm text-slate-400">Za posledních {windowDays} dní se nikdo nepřihlásil.</div>
              ) : (
                <div className="max-h-[480px] divide-y divide-slate-100 overflow-y-auto">
                  {topActiveFiltered.map((c) => (
                    <RadekKlienta
                      key={c.klient_id || c.telefon}
                      jmeno={c.klient_name || c.telefon}
                      doplnky={<>
                        <Pilulka>{c.login_count}×</Pilulka>
                        <Pilulka>{formatRelTime(c.last_login)}</Pilulka>
                      </>}
                    />
                  ))}
                </div>
              )}
            </KartaPrehledu>

            <KartaPrehledu>
              <HlavickaDlazdice
                ikona={Award}
                nazev="Aktivní celkem"
                napoveda="Všichni klienti, kteří se aspoň jednou přihlásili do aplikace, od zavedení sledování."
                odznak={allTimeActive.length}
              />
              {allTimeActive.length === 0 ? (
                <div className="py-6 text-center text-sm text-slate-400">Zatím se nikdo nepřihlásil.</div>
              ) : (
                <div className="max-h-[480px] divide-y divide-slate-100 overflow-y-auto">
                  {allTimeActive.map((c) => (
                    <RadekKlienta
                      key={c.klient_id || c.telefon}
                      jmeno={c.klient_name || c.telefon}
                      doplnky={<>
                        <Pilulka>{c.login_count}×</Pilulka>
                        <Pilulka>{formatRelTime(c.last_login)}</Pilulka>
                      </>}
                    />
                  ))}
                </div>
              )}
            </KartaPrehledu>
          </div>
        </>
      )}

      {data && sekce === "delaji" && <SekceChovani />}

      {data && sekce === "vypadli" && (
        <>
          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={AlertTriangle}
              nazev="Nikdy nepřihlášení"
              napoveda="Aktivní klienti, kteří se do aplikace nikdy nepřihlásili. Pro každého se jeho trenérovi automaticky založí úkol: zajistit přihlášení na začátku tréninku."
              odznak={neverLogged.length}
              odznakTon={neverLogged.length > 0 ? "cerveny" : "bily"}
            />
            {appOnboardingStats && appOnboardingStats.errorMessage && (
              <div className="border-b border-slate-200 p-3 sm:p-4">
                <Hlaska ton="problem" title="Automatické úkoly pro trenéry selhaly">
                  {appOnboardingStats.errorMessage}
                </Hlaska>
              </div>
            )}
            {appOnboardingStats && !appOnboardingStats.errorMessage
              && (appOnboardingStats.created > 0 || appOnboardingStats.skipped > 0 || appOnboardingStats.autoClosed > 0) && (
              <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 bg-slate-50 px-4 py-2 sm:px-5">
                <span className="text-xs font-semibold text-slate-600">Úkoly pro trenéry</span>
                {appOnboardingStats.created > 0 && <Pilulka>{appOnboardingStats.created} nově založeno</Pilulka>}
                {appOnboardingStats.skipped > 0 && <Pilulka>{appOnboardingStats.skipped} stále otevřeno</Pilulka>}
                {appOnboardingStats.autoClosed > 0 && <Pilulka>{appOnboardingStats.autoClosed} zavřeno samo</Pilulka>}
              </div>
            )}
            {neverLogged.length === 0 ? (
              <div className="p-3 sm:p-4">
                <Hlaska ton="ok" title="Všichni aktivní klienti se aspoň jednou přihlásili" />
              </div>
            ) : (
              <div className="max-h-[480px] divide-y divide-slate-100 overflow-y-auto">
                {neverLogged.map((c) => (
                  <RadekKlienta
                    key={c.notion_id || c.telefon}
                    jmeno={c.klient_name}
                    doplnky={<Pilulka>{c.lokalita || "bez lokality"}</Pilulka>}
                  />
                ))}
              </div>
            )}
          </KartaPrehledu>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={TrendingDown}
              nazev="Přestali chodit"
              napoveda="Klienti, kteří se přihlásili aspoň dvakrát, ale posledních třicet dní se neukázali. Stojí za to zkontrolovat, jestli mají pokračovat ve spolupráci."
              odznak={dropoff.length}
              odznakTon={dropoff.length > 0 ? "zluty" : "bily"}
            />
            {dropoff.length === 0 ? (
              <div className="p-3 sm:p-4">
                <Hlaska ton="ok" title="Nikdo nevypadl" />
              </div>
            ) : (
              <div className="max-h-[480px] divide-y divide-slate-100 overflow-y-auto">
                {dropoff.map((c) => (
                  <RadekKlienta
                    key={c.klient_id || c.telefon}
                    jmeno={c.klient_name || c.telefon}
                    doplnky={<>
                      <Pilulka>{c.login_count}×</Pilulka>
                      <Pilulka>{formatRelTime(c.last_login)}</Pilulka>
                    </>}
                  />
                ))}
              </div>
            )}
          </KartaPrehledu>
        </>
      )}

      {data && (
        <div className="text-center text-xs text-slate-400">
          Data z {data.generatedAt ? formatDateTime(data.generatedAt) : "neznámého času"}.
        </div>
      )}
    </Stranka>
  );
}

// ─── Co klienti v aplikaci dělají ──────────────────────────────────
// Události z portálu, načítají se až při otevření téhle části stránky.
function SekceChovani() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const nactenoRef = React.useRef(false);

  React.useEffect(() => {
    if (nactenoRef.current) return;
    nactenoRef.current = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await fetch("/api/behavioral-analytics", { credentials: "include" });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
        setData(d);
      } catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  const kpi = data?.kpi || {};
  const pageViews = data?.pageViews || [];
  const actions = data?.actions || [];
  const topClients = data?.topClients || [];
  const daily = data?.daily || [];
  const tableEmpty = data?._debug && (!data._debug.total_events || data._debug.total_events === 0);

  const dailyFilled = useMemo(() => {
    if (daily.length === 0) return [];
    const map = new Map(daily.map((d) => [d.day, d]));
    const out = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const dt = new Date(today.getTime() - i * 86400000);
      const key = dt.toISOString().substring(0, 10);
      const row = map.get(key);
      out.push({ day: key, label: fmtDate(dt), sessions: row ? row.sessions : 0, unique_users: row ? row.unique_users : 0 });
    }
    return out;
  }, [daily]);

  // Sekce s českým jménem a podílem proti nejnavštěvovanější.
  const sekceRadky = useMemo(() => {
    const max = Math.max(...pageViews.map((p) => p.views || 0), 1);
    return pageViews.map((p) => ({
      klic: p.name || "",
      nazev: SEKCE_APLIKACE[p.name] || p.name || "neznámá sekce",
      views: p.views || 0,
      lidi: p.unique_users || 0,
      podil: Math.max(2, Math.round(((p.views || 0) / max) * 100)),
    }));
  }, [pageViews]);

  // Akce seskupené podle části aplikace, uvnitř od nejčastějších.
  const skupinyAkci = useMemo(() => {
    const mapa = new Map();
    for (const a of actions) {
      const popis = AKCE_APLIKACE[a.name];
      const skupina = popis?.skupina || "Ostatní";
      if (!mapa.has(skupina)) mapa.set(skupina, []);
      mapa.get(skupina).push({
        klic: a.name,
        nazev: popis?.nazev || a.name || "neznámá akce",
        kliky: a.clicks || 0,
        lidi: a.unique_users || 0,
      });
    }
    return PORADI_SKUPIN
      .filter((s) => mapa.has(s))
      .map((s) => ({ skupina: s, polozky: mapa.get(s).sort((x, y) => y.kliky - x.kliky) }));
  }, [actions]);

  if (loading) return <SkeletonCards count={4} />;
  if (err) return <ErrorNote>Data o chování se nepodařilo načíst: {err}</ErrorNote>;
  if (data && tableEmpty) {
    return (
      <Hlaska ton="pozor" title="Zatím žádná data o chování">
        Sledování se začne plnit, jakmile se klienti přihlásí přes portál. Admin impersonace se nezapočítává.
      </Hlaska>
    );
  }
  if (!data) return null;

  return (
    <>
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Activity}
          nazev="Co dělají"
          podtitulek="souhrn za 30 dní"
          napoveda="Souhrn za posledních 30 dní. Otevření sekcí je každý přechod mezi částmi aplikace, akce jsou klepnutí na tlačítka, která něco udělají."
        />
        <div className="grid grid-cols-2 gap-2 p-3 sm:p-4 lg:grid-cols-3">
          <Dlazdice
            ton="znacka"
            popisek={<>Otevření sekcí<InfoTip text="Celkový počet přechodů mezi sekcemi aplikace za 30 dní." /></>}
            hodnota={fmt(kpi.page_views_30d || 0)}
          />
          <Dlazdice
            ton="zlata"
            popisek={<>Akcí<InfoTip text="Celkový počet klepnutí na sledovaná tlačítka za 30 dní." /></>}
            hodnota={fmt(kpi.actions_30d || 0)}
          />
          <Dlazdice
            ton="ok"
            popisek={<>Aktivních klientů<InfoTip text="Unikátní klienti, kteří za 30 dní udělali v aplikaci aspoň jednu věc." /></>}
            hodnota={fmt(kpi.unique_users_30d || 0)}
          />
          <Dlazdice
            ton="info"
            popisek={<>Návštěv 7 dní<InfoTip text="Počet návštěv aplikace za posledních 7 dní. Jedna návštěva je jedno přihlášení a práce v aplikaci." /></>}
            hodnota={fmt(kpi.sessions_7d || 0)}
          />
          <Dlazdice
            ton="info"
            popisek={<>Návštěv 30 dní<InfoTip text="Počet návštěv aplikace za posledních 30 dní." /></>}
            hodnota={fmt(kpi.sessions_30d || 0)}
          />
          <Dlazdice
            ton="neutralni"
            popisek={<>Běžná návštěva<InfoTip text="Medián délky jedné návštěvy v minutách za 30 dní. Návštěvy delší než hodina se nepočítají, bývá to aplikace zapomenutá na pozadí." /></>}
            hodnota={`${kpi.avg_session_min || 0} min`}
          />
        </div>
      </KartaPrehledu>

      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={LayoutGrid}
          nazev="Kam chodí"
          napoveda="Které sekce aplikace klienti otevírají. Proužek ukazuje podíl proti nejnavštěvovanější sekci, pilulka je počet lidí, kteří tam byli."
          odznak={sekceRadky.length > 0 ? `${sekceRadky.length} sekcí` : false}
        />
        {sekceRadky.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-400">Zatím žádná data.</div>
        ) : (
          <div className="space-y-3 p-3 sm:p-4">
            {sekceRadky.map((s) => (
              <div key={s.klic}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-sm font-bold text-slate-800">{s.nazev}</span>
                  <span className="shrink-0 whitespace-nowrap text-sm font-bold tabular-nums text-slate-900">{fmt(s.views)}×</span>
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <span className="block h-full rounded-full bg-brand" style={{ width: `${s.podil}%` }} />
                  </span>
                  <Pilulka>{s.lidi} lidí</Pilulka>
                </div>
              </div>
            ))}
          </div>
        )}
      </KartaPrehledu>

      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={MousePointerClick}
          nazev="Co udělali"
          napoveda="Sledovaná tlačítka v aplikaci, seskupená podle části, které se týkají. První pilulka je počet klepnutí, druhá počet lidí."
        />
        {skupinyAkci.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-400">Zatím žádné akce.</div>
        ) : (
          skupinyAkci.map((sk) => (
            <React.Fragment key={sk.skupina}>
              <SkupinaPrehledu nazev={sk.skupina} />
              <div className="divide-y divide-slate-100">
                {sk.polozky.map((a) => (
                  <div key={a.klic} className="flex items-center gap-2 px-4 py-2.5 sm:px-5">
                    <span className="min-w-0 flex-1 truncate text-sm text-slate-800">{a.nazev}</span>
                    <Pilulka>{fmt(a.kliky)}×</Pilulka>
                    <Pilulka>{a.lidi} lidí</Pilulka>
                  </div>
                ))}
              </div>
            </React.Fragment>
          ))
        )}
      </KartaPrehledu>

      {dailyFilled.length > 0 && (
        <KartaPrehledu>
          <HlavickaDlazdice
            ikona={Calendar}
            nazev="Návštěvy po dnech"
            napoveda="Počet návštěv aplikace po dnech za posledních 30 dní. Tenčí čára jsou unikátní klienti."
            odznak="30 dní"
          />
          <div className="p-3 sm:p-4">
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={dailyFilled} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(v, name) => [v, name === "sessions" ? "Návštěv" : "Unikátních klientů"]} />
                <Area type="monotone" dataKey="sessions" stroke="#10b981" fill="#10b981" fillOpacity={0.2} strokeWidth={2} />
                <Area type="monotone" dataKey="unique_users" stroke="#6E519F" fill="#6E519F" fillOpacity={0.1} strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </KartaPrehledu>
      )}

      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Clock}
          nazev="Nejvíc času v appce"
          podtitulek="za 30 dní"
          napoveda="Klienti s nejdelším souhrnným časem v aplikaci za posledních 30 dní."
        />
        {topClients.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-400">Zatím žádná data o návštěvách.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {topClients.map((c, i) => (
              <div key={c.klient_id || c.telefon || i} className="flex items-center gap-2 px-4 py-2.5 sm:px-5">
                <span className="w-5 shrink-0 text-sm tabular-nums text-slate-400">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800 demo-sensitive">
                  {c.klient_name || c.telefon || "—"}
                </span>
                <Pilulka>{c.sessions} návštěv</Pilulka>
                <Pilulka>{c.total_min} min</Pilulka>
              </div>
            ))}
          </div>
        )}
      </KartaPrehledu>
    </>
  );
}
